import React, { useState, useEffect } from 'react';
import { X, Lightbulb } from 'lucide-react';
import { useOnboarding } from './OnboardingContext';

/** Prefer `ArchiveHelperText` / `ArchiveEmptyState` (archive/) for museum-style guidance instead of floating cards. */
export const OnboardingHint = ({
  id,
  children,
  position = 'bottom',
  show = true,
  delay = 500,
}) => {
  const { isHintDismissed, dismissHint } = useOnboarding();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show && !isHintDismissed(id)) {
      const timer = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(timer);
    }
  }, [show, id, isHintDismissed, delay]);

  if (!visible || isHintDismissed(id)) {
    return null;
  }

  const positionClasses = {
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-[#e8e2d8]',
    bottom:
      'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-[#e8e2d8]',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-[#e8e2d8]',
    right:
      'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-[#e8e2d8]',
  };

  const handleDismiss = () => {
    setVisible(false);
    dismissHint(id);
  };

  return (
    <div className={`absolute ${positionClasses[position]} z-50 animate-fade-in`} data-testid={`hint-${id}`}>
      <div className="relative max-w-xs rounded-xl border border-[#e8e2d8] bg-[#fffcf7] px-4 py-3 shadow-[0_8px_30px_-12px_rgba(28,25,23,0.15)]">
        <div className={`absolute h-0 w-0 border-8 ${arrowClasses[position]}`} aria-hidden />

        <div className="flex items-start gap-3">
          <Lightbulb className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#c9a961]" aria-hidden />
          <div className="flex-1 text-sm leading-relaxed text-[#4a433c]">{children}</div>
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 text-[#8b7355] transition hover:text-[#1c1917]"
            data-testid={`hint-dismiss-${id}`}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const HintWrapper = ({ children, hint, hintId, hintPosition = 'bottom', showHint = true }) => {
  return (
    <div className="relative inline-block">
      {children}
      <OnboardingHint id={hintId} position={hintPosition} show={showHint}>
        {hint}
      </OnboardingHint>
    </div>
  );
};

export default OnboardingHint;
